import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  isSameMonth,
  isSameDay,
  isToday,
  parseISO
} from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  UtensilsCrossed,
  Stethoscope,
  Users,
  Pill,
  Grid3X3,
  List,
  CalendarRange,
  Calendar,
  Edit2,
  Trash2,
  Loader2,
  Sparkles,
  Check,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypeConfig = {
  school: { icon: GraduationCap, label: 'School', bgClass: 'bg-blue-100', textClass: 'text-blue-700', dotClass: 'bg-blue-500' },
  appointment: { icon: Stethoscope, label: 'Appointment', bgClass: 'bg-purple-100', textClass: 'text-purple-700', dotClass: 'bg-purple-500' },
  meal: { icon: UtensilsCrossed, label: 'Meal', bgClass: 'bg-orange-100', textClass: 'text-orange-700', dotClass: 'bg-orange-500' },
  handoff: { icon: Users, label: 'Handoff', bgClass: 'bg-pink-100', textClass: 'text-pink-700', dotClass: 'bg-pink-500' },
  medicine: { icon: Pill, label: 'Medicine', bgClass: 'bg-emerald-100', textClass: 'text-emerald-700', dotClass: 'bg-emerald-500' },
  event: { icon: Calendar, label: 'Event', bgClass: 'bg-violet-100', textClass: 'text-violet-700', dotClass: 'bg-violet-500' },
};

const CalendarPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // month, week, day
  const [filterTypes, setFilterTypes] = useState(['school', 'appointment', 'meal', 'handoff', 'medicine', 'event']);
  
  // Priority 4: Edit event state
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', date: '', time: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  // E7 Task G: AI Calendar Summary state
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [aiCallId, setAiCallId] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/calendar/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching calendar events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Priority 4: Open edit dialog for event
  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEditForm({
      title: event.title || '',
      date: event.date || '',
      time: event.time || '',
      notes: event.meta?.notes || ''
    });
  };

  // Priority 4: Save edited event - handles different event types
  const handleSaveEdit = async () => {
    if (!editingEvent) return;
    setSaving(true);
    try {
      const eventType = editingEvent.type;
      let endpoint = '';
      
      // Route to correct endpoint based on event type
      switch (eventType) {
        case 'school':
          endpoint = `${API}/school-tasks/${editingEvent.id}`;
          break;
        case 'appointment':
          endpoint = `${API}/appointments/${editingEvent.id}`;
          break;
        case 'meal':
          endpoint = `${API}/meals/${editingEvent.id}`;
          break;
        case 'event':
        default:
          endpoint = `${API}/calendar/events/${editingEvent.id}`;
      }
      
      await axios.put(endpoint, {
        ...editingEvent,
        title: editForm.title,
        task: editForm.title, // For school tasks
        name: editForm.title, // For meals
        date: editForm.date,
        time: editForm.time,
        notes: editForm.notes
      });
      
      toast.success('Event updated!');
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  // Delete event - handles different event types
  const handleDeleteEvent = async (event) => {
    const eventId = typeof event === 'string' ? event : event.id;
    const eventType = typeof event === 'object' ? event.type : 'event';
    
    setDeleting(eventId);
    try {
      let endpoint = '';
      
      // Route to correct endpoint based on event type
      switch (eventType) {
        case 'school':
          endpoint = `${API}/school-tasks/${eventId}`;
          break;
        case 'appointment':
          endpoint = `${API}/appointments/${eventId}`;
          break;
        case 'meal':
          endpoint = `${API}/meals/${eventId}`;
          break;
        case 'event':
        default:
          endpoint = `${API}/calendar/events/${eventId}`;
      }
      
      await axios.delete(endpoint);
      toast.success('Event deleted');
      setSelectedDate(null);
      fetchEvents();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete event');
    } finally {
      setDeleting(null);
    }
  };

  // E7 Task G: Get AI calendar summary for a date
  const getAIDaySummary = async (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    setAiSummaryLoading(true);
    setAiCallId(null);
    try {
      const response = await axios.get(`${API}/ai/calendar/daily-summary/${dateStr}`);
      setAiSummary({
        date: dateStr,
        ...response.data.summary
      });
      setAiCallId(response.data.call_id || null);
    } catch (error) {
      toast.error('Failed to generate summary');
    } finally {
      setAiSummaryLoading(false);
    }
  };

  // HITL: Confirm AI action
  const confirmAIAction = async (userAction) => {
    if (!aiCallId) return;
    try {
      await axios.post(`${API}/ai/confirm`, {
        call_id: aiCallId,
        user_action: userAction,
        edit_delta: []
      });
    } catch (error) {
      console.error('Failed to confirm AI action:', error);
    }
  };

  // Dismiss AI summary
  const dismissAISummary = async () => {
    await confirmAIAction('ignored');
    setAiSummary(null);
    setAiCallId(null);
  };

  const filteredEvents = useMemo(() => {
    return events.filter(event => filterTypes.includes(event.type));
  }, [events, filterTypes]);

  const getEventsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return filteredEvents.filter(event => event.date === dateStr);
  };

  const toggleFilter = (type) => {
    if (filterTypes.includes(type)) {
      setFilterTypes(filterTypes.filter(t => t !== type));
    } else {
      setFilterTypes([...filterTypes, type]);
    }
  };

  const navigatePrev = () => {
    if (viewMode === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    } else {
      setCurrentDate(addDays(currentDate, -1));
    }
  };

  const navigateNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      setCurrentDate(addDays(currentDate, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days for month view
  const generateMonthDays = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  };

  // Generate days for week view
  const generateWeekDays = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(weekStart, i));
    }
    return days;
  };

  const renderMonthView = () => {
    const days = generateMonthDays();
    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const weekDaysMobile = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

    return (
      <div className="bg-white rounded-3xl shadow-nurture overflow-hidden">
        {/* Week day headers */}
        <div className="grid grid-cols-7 border-b border-nurture-cream-dark">
          {weekDays.map((day, i) => (
            <div key={day} className="p-2 md:p-3 text-center text-xs md:text-sm font-medium text-nurture-muted">
              <span className="hidden md:inline">{day}</span>
              <span className="md:hidden">{weekDaysMobile[i]}</span>
            </div>
          ))}
        </div>
        
        {/* Calendar days */}
        <div className="grid grid-cols-7">
          {days.map((day, index) => {
            const dayEvents = getEventsForDate(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            
            return (
              <motion.div
                key={index}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedDate(day)}
                className={`
                  min-h-[60px] md:min-h-[100px] p-1 md:p-2 border-b border-r border-nurture-cream-dark cursor-pointer
                  transition-colors duration-200
                  ${!isCurrentMonth ? 'bg-nurture-cream/30' : 'bg-white hover:bg-nurture-cream/50'}
                  ${isSelected ? 'ring-2 ring-nurture-terracotta ring-inset' : ''}
                  ${isToday(day) ? 'bg-nurture-terracotta/5' : ''}
                `}
              >
                <div className={`
                  text-xs md:text-sm font-medium mb-1 w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-full mx-auto md:mx-0
                  ${isToday(day) ? 'bg-nurture-terracotta text-white' : ''}
                  ${!isCurrentMonth ? 'text-nurture-muted/50' : 'text-nurture-navy'}
                `}>
                  {format(day, 'd')}
                </div>
                
                {/* Desktop: show event titles */}
                <div className="hidden md:block space-y-1">
                  {dayEvents.slice(0, 3).map((event) => {
                    const config = eventTypeConfig[event.type];
                    return (
                      <div
                        key={event.id}
                        className={`text-xs px-1.5 py-0.5 rounded truncate ${config.bgClass} ${config.textClass}`}
                        title={event.title}
                      >
                        {event.title}
                      </div>
                    );
                  })}
                  {dayEvents.length > 3 && (
                    <div className="text-xs text-nurture-muted px-1">
                      +{dayEvents.length - 3} more
                    </div>
                  )}
                </div>
                
                {/* Mobile: show dots */}
                {dayEvents.length > 0 && (
                  <div className="md:hidden flex justify-center gap-0.5 mt-1">
                    {dayEvents.slice(0, 4).map((event, i) => {
                      const config = eventTypeConfig[event.type];
                      return (
                        <div
                          key={i}
                          className={`w-1.5 h-1.5 rounded-full ${config.dotClass}`}
                        />
                      );
                    })}
                    {dayEvents.length > 4 && (
                      <span className="text-[8px] text-nurture-muted">+</span>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const days = generateWeekDays();

    return (
      <div className="bg-white rounded-3xl shadow-nurture overflow-hidden">
        {/* Mobile: Horizontal scroll */}
        <div className="md:hidden overflow-x-auto">
          <div className="flex min-w-[700px]">
            {days.map((day, index) => {
              const dayEvents = getEventsForDate(day);
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              
              return (
                <div
                  key={index}
                  onClick={() => setSelectedDate(day)}
                  className={`
                    flex-1 min-h-[300px] border-r border-nurture-cream-dark cursor-pointer
                    ${isSelected ? 'ring-2 ring-nurture-terracotta ring-inset' : ''}
                    ${isToday(day) ? 'bg-nurture-terracotta/5' : ''}
                  `}
                >
                  <div className={`
                    p-2 border-b border-nurture-cream-dark text-center
                    ${isToday(day) ? 'bg-nurture-terracotta text-white' : ''}
                  `}>
                    <div className="text-xs uppercase">{format(day, 'EEE')}</div>
                    <div className="text-xl font-bold">{format(day, 'd')}</div>
                  </div>
                  <div className="p-1.5 space-y-1.5">
                    {dayEvents.map((event) => {
                      const config = eventTypeConfig[event.type];
                      const Icon = config.icon;
                      return (
                        <div key={event.id} className={`p-1.5 rounded-lg ${config.bgClass}`}>
                          <div className="flex items-start gap-1">
                            <Icon className={`w-3 h-3 mt-0.5 ${config.textClass}`} />
                            <p className={`text-xs font-medium truncate ${config.textClass}`}>
                              {event.title}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Desktop: Full grid */}
        <div className="hidden md:grid grid-cols-7 divide-x divide-nurture-cream-dark">
          {days.map((day, index) => {
            const dayEvents = getEventsForDate(day);
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            
            return (
              <div
                key={index}
                onClick={() => setSelectedDate(day)}
                className={`
                  min-h-[400px] cursor-pointer
                  ${isSelected ? 'ring-2 ring-nurture-terracotta ring-inset' : ''}
                  ${isToday(day) ? 'bg-nurture-terracotta/5' : 'hover:bg-nurture-cream/30'}
                `}
              >
                <div className={`
                  p-3 border-b border-nurture-cream-dark text-center
                  ${isToday(day) ? 'bg-nurture-terracotta text-white' : ''}
                `}>
                  <div className="text-xs uppercase tracking-wide">
                    {format(day, 'EEE')}
                  </div>
                  <div className="text-2xl font-bold">
                    {format(day, 'd')}
                  </div>
                </div>
                
                <ScrollArea className="h-[350px] p-2">
                  <div className="space-y-2">
                    {dayEvents.map((event) => {
                      const config = eventTypeConfig[event.type];
                      const Icon = config.icon;
                      return (
                        <div
                          key={event.id}
                          className={`p-2 rounded-lg ${config.bgClass}`}
                        >
                          <div className="flex items-start gap-2">
                            <Icon className={`w-4 h-4 mt-0.5 ${config.textClass}`} />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${config.textClass}`}>
                                {event.title}
                              </p>
                              {event.time && (
                                <p className="text-xs text-nurture-muted">{event.time}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {dayEvents.length === 0 && (
                      <p className="text-xs text-nurture-muted text-center py-4">
                        No events
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const dayEvents = getEventsForDate(currentDate);
    
    // Group events by type
    const groupedEvents = dayEvents.reduce((acc, event) => {
      if (!acc[event.type]) acc[event.type] = [];
      acc[event.type].push(event);
      return acc;
    }, {});

    return (
      <div className="bg-white rounded-3xl shadow-nurture overflow-hidden">
        <div className={`p-6 text-center ${isToday(currentDate) ? 'bg-nurture-terracotta text-white' : 'bg-nurture-cream'}`}>
          <div className="text-sm uppercase tracking-wide">
            {format(currentDate, 'EEEE')}
          </div>
          <div className="text-4xl font-bold">
            {format(currentDate, 'd')}
          </div>
          <div className="text-sm">
            {format(currentDate, 'MMMM yyyy')}
          </div>
          {/* E7: AI Summary Button */}
          <Button
            onClick={() => getAIDaySummary(currentDate)}
            disabled={aiSummaryLoading}
            variant="outline"
            size="sm"
            className={`mt-3 rounded-full ${isToday(currentDate) ? 'border-white/50 text-white hover:bg-white/10' : ''}`}
            data-testid="ai-day-summary-btn"
          >
            {aiSummaryLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            AI Summary
          </Button>
        </div>
        
        <div className="p-6">
          {/* E7: AI Summary Card */}
          {aiSummary && aiSummary.date === format(currentDate, 'yyyy-MM-dd') && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl border border-cyan-200"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-cyan-600" />
                  <h4 className="font-medium text-nurture-navy">AI Day Summary</h4>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={dismissAISummary}
                  className="h-6 w-6 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {aiSummary.summary && (
                <p className="text-sm text-nurture-navy mb-3">{aiSummary.summary}</p>
              )}
              
              {aiSummary.busiest_period && (
                <p className="text-xs text-cyan-700 mb-2">
                  <strong>Busiest:</strong> {aiSummary.busiest_period}
                </p>
              )}
              
              {aiSummary.tips?.length > 0 && (
                <div className="text-xs text-cyan-700">
                  <strong>Tips:</strong>
                  <ul className="list-disc list-inside mt-1">
                    {aiSummary.tips.slice(0, 3).map((tip, i) => (
                      <li key={i}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          )}
          
          {dayEvents.length === 0 ? (
            <div className="text-center py-12 text-nurture-muted">
              <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No events scheduled for this day</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedEvents).map(([type, typeEvents]) => {
                const config = eventTypeConfig[type];
                const Icon = config.icon;
                
                return (
                  <div key={type}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-8 h-8 rounded-lg ${config.bgClass} flex items-center justify-center`}>
                        <Icon className={`w-4 h-4 ${config.textClass}`} />
                      </div>
                      <h3 className="font-heading font-bold text-nurture-navy">{config.label}</h3>
                      <Badge variant="outline">{typeEvents.length}</Badge>
                    </div>
                    <div className="space-y-2 ml-10">
                      {typeEvents.map((event) => (
                        <Card key={event.id} className="rounded-xl shadow-sm">
                          <CardContent className="p-4">
                            <p className="font-medium text-nurture-navy">{event.title}</p>
                            {event.time && (
                              <p className="text-sm text-nurture-muted">{event.time}</p>
                            )}
                            {event.meta && Object.entries(event.meta).map(([key, value]) => {
                              if (!value) return null;
                              return (
                                <p key={key} className="text-sm text-nurture-muted capitalize">
                                  {key.replace('_', ' ')}: {String(value)}
                                </p>
                              );
                            })}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Selected date modal
  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-nurture-navy flex items-center gap-2 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                <CalendarDays className="w-4 h-4 md:w-5 md:h-5 text-cyan-600" />
              </div>
              Family Calendar
            </h1>
            <p className="text-nurture-muted mt-1 text-sm md:text-base">All your family events in one view</p>
          </div>

          {/* View mode toggle - Desktop */}
          <div className="hidden md:flex items-center gap-2 bg-nurture-cream-dark rounded-full p-1">
            <Button
              variant={viewMode === 'month' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('month')}
              className={`rounded-full ${viewMode === 'month' ? 'bg-white shadow-sm' : ''}`}
              data-testid="view-month"
            >
              <Grid3X3 className="w-4 h-4 mr-1" />
              Month
            </Button>
            <Button
              variant={viewMode === 'week' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('week')}
              className={`rounded-full ${viewMode === 'week' ? 'bg-white shadow-sm' : ''}`}
              data-testid="view-week"
            >
              <CalendarRange className="w-4 h-4 mr-1" />
              Week
            </Button>
            <Button
              variant={viewMode === 'day' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('day')}
              className={`rounded-full ${viewMode === 'day' ? 'bg-white shadow-sm' : ''}`}
              data-testid="view-day"
            >
              <List className="w-4 h-4 mr-1" />
              Day
            </Button>
          </div>
        </div>

        {/* View mode toggle - Mobile */}
        <div className="md:hidden flex items-center gap-1 bg-nurture-cream-dark rounded-full p-1 self-start">
          <Button
            variant={viewMode === 'month' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('month')}
            className={`rounded-full px-3 h-8 text-xs ${viewMode === 'month' ? 'bg-white shadow-sm' : ''}`}
            data-testid="view-month-mobile"
          >
            <Grid3X3 className="w-3 h-3 mr-1" />
            Month
          </Button>
          <Button
            variant={viewMode === 'week' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('week')}
            className={`rounded-full px-3 h-8 text-xs ${viewMode === 'week' ? 'bg-white shadow-sm' : ''}`}
            data-testid="view-week-mobile"
          >
            <CalendarRange className="w-3 h-3 mr-1" />
            Week
          </Button>
          <Button
            variant={viewMode === 'day' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('day')}
            className={`rounded-full px-3 h-8 text-xs ${viewMode === 'day' ? 'bg-white shadow-sm' : ''}`}
            data-testid="view-day-mobile"
          >
            <List className="w-3 h-3 mr-1" />
            Day
          </Button>
        </div>
      </div>

      {/* Navigation and filters */}
      <div className="flex flex-col gap-3">
        {/* Navigation */}
        <div className="flex items-center justify-between md:justify-start gap-2 md:gap-3">
          <Button variant="outline" size="icon" onClick={navigatePrev} className="rounded-full h-8 w-8 md:h-10 md:w-10" data-testid="nav-prev">
            <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
          </Button>
          <h2 className="text-base md:text-xl font-heading font-bold text-nurture-navy text-center flex-1 md:flex-none md:min-w-[200px]">
            {viewMode === 'month' && format(currentDate, 'MMMM yyyy')}
            {viewMode === 'week' && (
              <span className="hidden md:inline">Week of {format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'MMM d, yyyy')}</span>
            )}
            {viewMode === 'week' && (
              <span className="md:hidden">{format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'MMM d')} - {format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'd')}</span>
            )}
            {viewMode === 'day' && (
              <span className="hidden md:inline">{format(currentDate, 'EEEE, MMMM d, yyyy')}</span>
            )}
            {viewMode === 'day' && (
              <span className="md:hidden">{format(currentDate, 'EEE, MMM d')}</span>
            )}
          </h2>
          <Button variant="outline" size="icon" onClick={navigateNext} className="rounded-full h-8 w-8 md:h-10 md:w-10" data-testid="nav-next">
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
          </Button>
          <Button variant="outline" onClick={goToToday} className="rounded-full text-xs md:text-sm px-3 h-8 md:h-10" data-testid="go-today">
            Today
          </Button>
        </div>

        {/* Filters - scrollable on mobile */}
        <div className="overflow-x-auto pb-1 -mx-4 px-4 md:mx-0 md:px-0">
          <div className="flex gap-2 min-w-max">
            {Object.entries(eventTypeConfig).map(([type, config]) => {
              const Icon = config.icon;
              const isActive = filterTypes.includes(type);
              return (
                <Button
                  key={type}
                  variant={isActive ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleFilter(type)}
                  className={`rounded-full text-xs md:text-sm whitespace-nowrap ${isActive ? config.bgClass + ' ' + config.textClass + ' border-transparent' : ''}`}
                  data-testid={`filter-${type}`}
                >
                  <Icon className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                  {config.label}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Calendar */}
      {loading ? (
        <div className="h-96 bg-nurture-cream-dark rounded-3xl animate-pulse" />
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={viewMode + currentDate.toISOString()}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {viewMode === 'month' && renderMonthView()}
            {viewMode === 'week' && renderWeekView()}
            {viewMode === 'day' && renderDayView()}
          </motion.div>
        </AnimatePresence>
      )}

      {/* Legend */}
      <Card className="rounded-3xl shadow-nurture">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium text-nurture-muted">Legend:</span>
            {Object.entries(eventTypeConfig).map(([type, config]) => (
              <div key={type} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${config.dotClass}`} />
                <span className="text-sm text-nurture-navy">{config.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Selected date modal */}
      <Dialog open={!!selectedDate && viewMode === 'month'} onOpenChange={() => setSelectedDate(null)}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {selectedDateEvents.length === 0 ? (
              <div className="text-center py-8 text-nurture-muted">
                <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No events for this day</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedDateEvents.map((event) => {
                  const config = eventTypeConfig[event.type];
                  const Icon = config.icon;
                  return (
                    <div key={event.id} className={`p-4 rounded-xl ${config.bgClass}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-lg bg-white/50 flex items-center justify-center`}>
                          <Icon className={`w-4 h-4 ${config.textClass}`} />
                        </div>
                        <div className="flex-1">
                          <p className={`font-medium ${config.textClass}`}>{event.title}</p>
                          {event.time && <p className="text-sm opacity-75">{event.time}</p>}
                          {event.meta?.child_name && <p className="text-sm opacity-75">Child: {event.meta.child_name}</p>}
                          {event.meta?.location && <p className="text-sm opacity-75">Location: {event.meta.location}</p>}
                          {event.meta?.notes && <p className="text-sm opacity-75 mt-1">{event.meta.notes}</p>}
                        </div>
                        {/* Edit/Delete for ALL event types */}
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleEditEvent(event)}
                            className="h-8 w-8 opacity-60 hover:opacity-100"
                            data-testid={`edit-event-${event.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDeleteEvent(event)}
                            disabled={deleting === event.id}
                            className="h-8 w-8 text-red-500 hover:text-red-700 opacity-60 hover:opacity-100"
                            data-testid={`delete-event-${event.id}`}
                          >
                            {deleting === event.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Priority 4: Edit Event Dialog */}
      <Dialog open={!!editingEvent} onOpenChange={() => setEditingEvent(null)}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-violet-500" />
              Edit Event
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                placeholder="Event title"
                className="rounded-xl"
                data-testid="edit-event-title"
              />
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={editForm.date}
                onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                className="rounded-xl"
                data-testid="edit-event-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Time</Label>
              <Input
                type="time"
                value={editForm.time}
                onChange={(e) => setEditForm({ ...editForm, time: e.target.value })}
                className="rounded-xl"
                data-testid="edit-event-time"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Additional notes"
                className="rounded-xl"
                data-testid="edit-event-notes"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleSaveEdit}
                disabled={saving || !editForm.title || !editForm.date}
                className="flex-1 bg-violet-500 hover:bg-violet-600 rounded-xl"
                data-testid="save-edit-event"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
              <Button
                variant="outline"
                onClick={() => setEditingEvent(null)}
                className="rounded-xl"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
